import { Color, LicenseTester, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, ClickEvent, Panel, Table, TableRow } from "@mcleod/components";

const log = LogManager.getLogger("lme.dispatch.FilterPanel");

export interface SecondaryFilterProps {
    id: string;
    caption: string;
    filter: (row: ModelRow) => boolean;
    visible?: (data: any) => boolean;
    imageName?: ImageName;
    imageColor?: string;
    dynamic?: boolean;
    licensedClass?: string; // If a class is provided, it is tested and any provided package is ignored
    licensedPackage?: string; // Tested only if the licensedClass is not provided
}

export interface SecondaryFilter extends SecondaryFilterProps {
    active?: boolean;
    count?: number;
    button?: Button;
}

export class FilterPanel {
    public filters: SecondaryFilter[];
    private table: Table;
    public panel: Panel;
    private onFilter: (filter: SecondaryFilter, table: Table) => void;
    private updateInterval: number;
    private _isUpdatingCounts: boolean = false;
    private _dataChanged: boolean = true;

    constructor(filters: SecondaryFilterProps[], table: Table, panel: Panel, onFilter?: (filter: SecondaryFilter, table: Table) => void) {
        log.debug("Setting up secondary filters", filters);
        this.table = table;
        this.panel = panel;
        this.onFilter = onFilter;

        this.panel.components = [];
        this.initializeFilters(filters);

        this.table.dataSource.addRemoteDataChangeListener(() => {
            this.dataChanged = true;
        });

        this.table.dataSource.addDisplayListener(event => this.updateFilterCounts());
        this.addIntervalOnMount();
    }

    private addIntervalOnMount() {
        this.panel.addMountListener(() => {
            this.startUpdateInterval();
            this.panel.addUnmountListener(() => {
                this.stopUpdateInterval();
                this.addIntervalOnMount();
            });
        });
    }

    private startUpdateInterval() {
        this.updateInterval = window.setInterval(() => {
            if (this.dataChanged) {
                if (!this.panel.isMounted) {
                    return;
                }
                if (this.isUpdatingCounts) {
                    return;
                }

                try {
                    this.isUpdatingCounts = true;
                    this.updateFilterCounts();
                } finally {
                    this.dataChanged = false;
                    this.isUpdatingCounts = false;
                }
            }
        }, 500);
    }

    private stopUpdateInterval() {
        if (this.updateInterval) {
            window.clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }

    private initializeFilters(filters: SecondaryFilterProps[]) {
        this.filters = [];
        for (const filter of filters) {
            if (this.testLicense(filter)) {
                log.debug("Found license for filter", filter.id);
                this.addFilter(filter);
            }
        }
    }

    /**
     * Tests if the filter should be available based on licensing.
     * - If a licensed class is provided in the filter, that class name will be tested.  Any provided licensed package
     *   will be ignored.
     * - If no licensed class name is provided in the filter, and a licensed package is provided, that package name will
     *   be tested.
     * - Otherwise, the filter will be considered to be available.
     * @param filter The SecondaryFilterProps to be tested
     * @returns A boolean indicating if the filter should be made available based on licensing
     */
    private testLicense(filter: SecondaryFilterProps): boolean {
        if (!StringUtil.isEmptyString(filter.licensedClass)) {
            return LicenseTester.testClass(filter.licensedClass);
        }
        if (!StringUtil.isEmptyString(filter.licensedPackage)) {
            return LicenseTester.testPackage(filter.licensedPackage);
        }
        return true;
    }

    addFilter(filter: SecondaryFilterProps) {
        const filterExists = this.filters.find(f => f.id === filter.id);
        if (filterExists) {
            log.error(`Filter with id ${filter.id} already exists`);
            return;
        }
        const newFilter = {
            ...filter,
            active: false,
            count: 0,
            button: null
        };
        const btn = this.getSecondaryFilterButton(newFilter);
        newFilter.button = btn;
        this.panel.add(btn);
        this.filters.push(newFilter);
    }

    public updateFilterCounts() {
        for (const filter of this.filters) {
            filter["_newCount"] = 0; // a temporary property to track the updated counts
        }
        for (const row of this.table.dataSource.data) {
            for (const filter of this.filters) {
                if (filter.filter(row)) {
                    filter["_newCount"]++;
                }
            }
        }
        for (const filter of this.filters) {
            filter.count = filter["_newCount"];
            delete filter["_newCount"]; // clean up temporary property
            filter.button.caption = `${filter.caption} ${filter.count}`;
        }
    }

    public syncFilterVisibility(data: any) {
        for (const filter of this.filters) {
            if (filter.visible != null) {
                filter.button.visible = filter.visible(data);
            }
        }
    }

    public passesActiveFilter(modelRow: ModelRow): boolean {
        const active = this.getActiveFilter();
        if (active == null)
            return true;

        return active.filter(modelRow);
    }

    public clearActiveFilter() {
        const active = this.getActiveFilter();
        if (active != null)
            this.toggleSecondaryFilter(null, active);
    }

    private getSecondaryFilterButton(sFilter: SecondaryFilter): Button {
        return new Button({
            id: `btn${sFilter.id}`,
            caption: sFilter.caption,
            imageName: sFilter?.imageName,
            imageColor: sFilter?.imageColor,
            rowBreak: false,
            color: "McLeodTertiary",
            marginRight: 12,
            paddingTop: 6,
            paddingBottom: 6,
            wrap: false,
            onClick: (event => this.toggleSecondaryFilter(event, sFilter))
        });
    }

    private toggleSecondaryFilter(event: ClickEvent, sFilter: SecondaryFilter) {
        log.debug(`Toggling secondary filter: ${sFilter.caption}`);
        const activeFilter = this.getActiveFilter();

        if (activeFilter == null || activeFilter.id !== sFilter.id) {
            if (activeFilter != null) {
                activeFilter.active = false;
                this.updateFilterBtnStyle(activeFilter.button, false, activeFilter?.imageColor);
            }
            sFilter.active = true;
            this.updateFilterBtnStyle(sFilter.button, true, sFilter?.imageColor);
        } else {
            sFilter.active = false;
            this.updateFilterBtnStyle(sFilter.button, false, sFilter?.imageColor);
        }

        this.applyRemoveFilter(sFilter);
    }

    public toggleFilterById(id: string) {
        const filter = this.filters.find(filter => filter.id === id);
        if (filter != null && !filter.active) {
            this.toggleSecondaryFilter(null, filter);
        } else {
            log.error(`Unable to find filter with id: ${id}`);
        }
    }

    public applyRemoveFilter(filter: SecondaryFilter) {
        if (filter == null) {
            return;
        }
        const trFilter = (tableRow: TableRow) => {
            const modelRow = tableRow.data;
            return filter.filter(modelRow);
        };
        filter.active ? this.table.customFilter = trFilter : this.table.clearCustomFilter();
        if (this.onFilter) {
            this.onFilter(filter, this.table);
        }
    }

    private updateFilterBtnStyle(btn: Button, selected: boolean, imageColor: Color) {
        btn.color = selected ? "success.reverse" : "McLeodTertiary";
        btn.backgroundColor = selected ? "success" : null;
        btn.imageColor = selected ? "success.reverse" : imageColor;
    }

    public getActiveFilter(): SecondaryFilter {
        return this.filters.find(filter => filter.active);
    }

    public removeFilterFromPanel(filter: SecondaryFilter) {
        if (filter.active) {
            this.applyRemoveFilter(filter);
        }
        this.panel.remove(filter.button);
        this.filters = this.filters.filter(f => f.id !== filter.id);
    }

    public createFilter(filter: SecondaryFilterProps): SecondaryFilter {
        const newFilter = {
            ...filter,
            active: false,
            count: 0,
            button: null
        };
        const btn = this.getSecondaryFilterButton(newFilter);
        newFilter.button = btn;
        return newFilter;
    }

    public get isUpdatingCounts(): boolean {
        return this._isUpdatingCounts;
    }

    public set isUpdatingCounts(value: boolean) {
        this._isUpdatingCounts = value;
    }

    public get dataChanged(): boolean {
        return this._dataChanged;
    }

    public set dataChanged(value: boolean) {
        this._dataChanged = value;
    }
}
